import { MetricsChart } from 'components/MetricsChart';
import { BarChartRenderer } from 'components/chart/renderer/BarChartRenderer';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { ChartDataComparisonType } from 'components/chart/data/ChartDataComparisonType';
import { useIntl } from 'react-intl';
import { formatDateRangeLabel } from 'pages/insights-pro/country-tab/util';
import { MarketplaceInfo } from 'pages/insights-pro/marketplace-tab/model';
import { getArrayWithIsOthers } from 'pages/insights-pro/common/graphs/getArrayWithIsOthers';
import { TileContent } from 'components/Layout/PageContainer';
import { DataResult } from 'models/data';
import { useModelService } from 'services/model/model.service';
import { useAppStore } from 'hooks/state/useAppStore';
import { EmptyState } from 'components/EmptyState';
import { groupTopNWithOthers, GroupedEntry } from 'pages/insights-pro/product-tab/utils/groupTopNWithOthers';

interface MarketplacesReturnRatesProps {
  total: DataResult;
  marketplaces: MarketplaceInfo[];
  isComparisonActive: boolean;
  testId?: string;
}

export function NetSalesRatesBarChart({
  total,
  marketplaces,
  isComparisonActive,
  testId = '',
}: MarketplacesReturnRatesProps) {
  const intl = useIntl();

  const { dateRange } = useAppStore();

  const { getMeasuresByMetrics } = useModelService();
  const [netSlsGmvValRate] = getMeasuresByMetrics(['netSlsGmvValRate'], 'OD');
  if (marketplaces.length === 0) {
    return <EmptyState />;
  }
  const groupedMarketPlaces = getArrayWithIsOthers(marketplaces, [netSlsGmvValRate.code], intl);

  // Convert marketplace data to GroupedEntry format
  const groupedEntries: GroupedEntry[] = groupedMarketPlaces.map(marketplace => ({
    name: marketplace.name,
    value: netSlsGmvValRate.getValue(marketplace.entry.values) || 0,
    compareValue: netSlsGmvValRate.getValue(marketplace.entry.compareValues) || 0,
  }));

  // Use groupTopNWithOthers with average aggregation for rates
  const finalMarketPlaces = groupTopNWithOthers(groupedEntries, 10, intl, 'avg').sort((a, b) => a.value - b.value);

  const createReturnRateChartConfig = () => {
    const returnRateChartConfig = BarChartConfig.forTBMeasure(netSlsGmvValRate);

    returnRateChartConfig.data = [
      new ChartDataArray(
        ChartDataType.primary,
        formatDateRangeLabel(dateRange.range, intl),
        finalMarketPlaces.map(marketplace => new ChartData(marketplace.name, marketplace.value))
      ),
    ];

    if (isComparisonActive) {
      returnRateChartConfig.data.push(
        new ChartDataArray(
          ChartDataType.primary,
          formatDateRangeLabel(dateRange.comparison, intl),
          finalMarketPlaces.map(marketplace => new ChartData(marketplace.name, marketplace.compareValue)),
          ChartDataComparisonType.comparison
        )
      );
    }

    returnRateChartConfig.data.push(
      new ChartDataArray(
        ChartDataType.average,
        intl.formatMessage({ id: 'general.label.total' }),
        [netSlsGmvValRate.getValue(total.values)].map(avg => new ChartData('AVG', avg))
      )
    );

    return returnRateChartConfig;
  };

  const returnRateChartRenderer = new BarChartRenderer(createReturnRateChartConfig());
  return (
    <TileContent className="px-0 return-rates">
      <MetricsChart renderer={returnRateChartRenderer} height="h-[400px]" testId={testId} />
    </TileContent>
  );
}
